import React from 'react';
import { LegalLayout } from '../components/LegalLayout';

export function TermsOfService() {
  return (
    <LegalLayout>
      <div className="prose prose-invert prose-lg max-w-none">
        <h1 className="text-4xl font-outfit font-bold mb-8 text-pearl-white">Naia Terms of Use</h1>
        
        <div className="mb-6 text-slate-grey">
          <p><strong>Effective Date:</strong> 2025-06-03</p>
          <p><strong>Last Updated:</strong> 2025-06-03</p>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">1. Acceptance of Terms</h2>
          <p className="mb-4">
            Welcome to Naia, a streaming platform dedicated to vocal synthesis music. These Terms of Use ("Terms") constitute a legally binding agreement between you and Naia, a company registered in Sweden, regarding your use of our platform at streamnaia.com and our mobile applications (collectively, the "Service").
          </p>
          <p>
            By accessing or using our Service, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use our Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">2. Description of Service</h2>
          <p className="mb-4">
            Naia is a music streaming platform that specializes in vocal synthesis music, including but not limited to Vocaloid, SynthV, UTAU, and other digital vocal technologies. Our Service allows users to:
          </p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Stream vocal synthesis music</li>
            <li>Discover new artists and tracks</li>
            <li>Create and manage playlists</li>
            <li>Follow artists and other users</li>
            <li>Upload and share original vocal synthesis compositions (for approved creators)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">3. Eligibility and Account Requirements</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">3.1 Age Requirements</h3>
          <p className="mb-6">You must be at least 13 years old to use our Service. If you are under 18, you represent that you have obtained parental or guardian consent to use our Service.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">3.2 Account Creation</h3>
          <p className="mb-4">To access certain features, you must create an account by providing:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li>A valid email address</li>
            <li>A unique username</li>
            <li>Your name</li>
          </ul>
          <p className="mb-6">You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">3.3 Account Accuracy</h3>
          <p className="mb-6">You agree to provide accurate, current, and complete information and to update such information as necessary to maintain its accuracy.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">4. Subscription Plans and Payment Terms</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">4.1 Service Tiers</h3>
          <p className="mb-4">Naia offers two service tiers:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Free Tier:</strong> Limited features with advertisements</li>
            <li><strong>Premium Tier:</strong> €8.99 per month or €89.99 per year (paid annually)</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">4.2 Payment Processing</h3>
          <p className="mb-6">All payments are processed securely through Stripe. By subscribing to our Premium Tier, you authorize us to charge your selected payment method.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">4.3 Billing and Renewal</h3>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Monthly subscriptions automatically renew monthly</li>
            <li>Annual subscriptions automatically renew annually</li>
            <li>You may cancel your subscription at any time</li>
            <li>Cancellations take effect at the end of your current billing period</li>
            <li>No refunds are provided for partial billing periods</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">4.4 Price Changes</h3>
          <p className="mb-6">We reserve the right to modify subscription prices with 30 days' notice to existing subscribers.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">5. Content Guidelines and Acceptable Use</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.1 Prohibited Content</h3>
          <p className="mb-4">The following content is strictly prohibited on Naia and will result in immediate account termination and deletion:</p>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">5.1.1 Sexual Content</h4>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Overly sexual cover art or profile pictures/headers</li>
            <li>Content depicting genitalia or exposed buttocks</li>
            <li>Artistic nudity is permitted if it follows the above restrictions</li>
          </ul>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">5.1.2 Child Safety (Zero Tolerance)</h4>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Child Sexual Abuse Material (CSAM) - results in immediate account deletion, permanent IP block, and police report filed within 48 hours</li>
            <li>Artistic depictions of children in sexual environments or situations (including "loli/shota" content) - immediate ban and account deletion</li>
          </ul>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">5.1.3 Violence and Gore</h4>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Real-life photographs of gore, injured people, or injured animals</li>
            <li>Realistically drawn gore or violence</li>
            <li>Drawn gore in non-realistic styles may be permitted at our discretion</li>
          </ul>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">5.1.4 Additional Prohibited Content</h4>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Hate speech or content promoting discrimination</li>
            <li>Harassment or bullying</li>
            <li>Spam or malicious content</li>
            <li>Content that violates intellectual property rights</li>
            <li>Illegal content or content promoting illegal activities</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.2 Content Review Process</h3>
          <p className="mb-6">All uploaded content is subject to our review process before publication. We reserve the right to reject any content that violates these guidelines.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.3 User Conduct</h3>
          <p className="mb-4">You agree not to:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Impersonate others or provide false information</li>
            <li>Attempt to circumvent our technical measures</li>
            <li>Reverse engineer or attempt to access our source code</li>
            <li>Use automated tools to access our Service without permission</li>
            <li>Interfere with the proper functioning of our Service</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">6. Intellectual Property Rights</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">6.1 Your Content</h3>
          <p className="mb-4">You retain ownership of the intellectual property rights in content you upload. By uploading content, you grant Naia a non-exclusive, worldwide, royalty-free license to:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Host, store, and distribute your content</li>
            <li>Display your content to other users</li>
            <li>Promote your content within our Service</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">6.2 Our Content</h3>
          <p className="mb-4">Naia and its licensors own all rights in the Service, including but not limited to:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>The Naia platform and software</li>
            <li>Our trademarks, logos, and branding</li>
            <li>The compilation and organization of content</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">6.3 Copyright Infringement</h3>
          <p className="mb-4">We respect intellectual property rights and expect our users to do the same.</p>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">6.3.1 DMCA Compliance</h4>
          <p className="mb-4">If you believe your copyrighted work has been infringed, please contact:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li><strong>Email:</strong> dmca@streamnaia.com</li>
            <li><strong>General Legal Inquiries:</strong> legal@streamnaia.com</li>
          </ul>

          <h4 className="text-lg font-outfit font-medium mb-2 text-pearl-white">6.3.2 Required Information for DMCA Notices</h4>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Identification of the copyrighted work claimed to be infringed</li>
            <li>Identification of the infringing material and its location on our Service</li>
            <li>Your contact information</li>
            <li>A statement of good faith belief that the use is not authorized</li>
            <li>A statement that the information is accurate and you are authorized to act</li>
            <li>Your physical or electronic signature</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">7. Beta Period Disclosure</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">7.1 Current Beta Status</h3>
          <p className="mb-4">Naia is currently in beta. During this period:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Artist compensation features are not yet implemented</li>
            <li>Some features may be limited or unavailable</li>
            <li>The Service may experience interruptions or technical issues</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">7.2 Future Data Collection</h3>
          <p className="mb-4">We are transparent that we do not currently collect listening history or device information. When we implement artist compensation features, we will:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Notify all users in advance</li>
            <li>Update our Privacy Policy</li>
            <li>Obtain necessary consents for additional data collection</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">8. Privacy and Data Protection</h2>
          <p className="mb-6">Your privacy is important to us. Please review our Privacy Policy, which is incorporated into these Terms by reference, to understand how we collect, use, and protect your information.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">9. Service Availability and Modifications</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">9.1 Service Availability</h3>
          <p className="mb-6">We strive to maintain Service availability but do not guarantee uninterrupted access. We may temporarily suspend the Service for maintenance, updates, or technical issues.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">9.2 Service Modifications</h3>
          <p className="mb-6">We reserve the right to modify, update, or discontinue features of our Service at any time with reasonable notice.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">10. Termination</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">10.1 Termination by You</h3>
          <p className="mb-6">You may terminate your account at any time by contacting legal@streamnaia.com.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">10.2 Termination by Us</h3>
          <p className="mb-4">We may terminate or suspend your account immediately for:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Violation of these Terms</li>
            <li>Prohibited content or conduct</li>
            <li>Extended inactivity</li>
            <li>Legal or regulatory requirements</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">10.3 Effect of Termination</h3>
          <p className="mb-4">Upon termination:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Your access to the Service will cease</li>
            <li>Your content may be removed from our Service</li>
            <li>Certain provisions of these Terms will survive termination</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">11. Disclaimers and Limitation of Liability</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">11.1 Service Disclaimers</h3>
          <p className="mb-6">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">11.2 Limitation of Liability</h3>
          <p className="mb-6">TO THE MAXIMUM EXTENT PERMITTED BY LAW, NAIA SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR USE, ARISING FROM YOUR USE OF THE SERVICE.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">11.3 Maximum Liability</h3>
          <p className="mb-6">Our total liability to you for any claims arising from these Terms or your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">12. Indemnification</h2>
          <p className="mb-4">You agree to indemnify and hold harmless Naia, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including attorney fees) arising from:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Content you upload or share</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">13. Dispute Resolution</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">13.1 Governing Law</h3>
          <p className="mb-6">These Terms are governed by Swedish law, without regard to conflict of law principles.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">13.2 Jurisdiction</h3>
          <p className="mb-6">Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of Sweden.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">13.3 EU Residents</h3>
          <p className="mb-6">If you are an EU resident, you may also bring proceedings in the courts of your country of residence.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">14. General Provisions</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">14.1 Entire Agreement</h3>
          <p className="mb-6">These Terms, together with our Privacy Policy, constitute the entire agreement between you and Naia regarding the Service.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">14.2 Severability</h3>
          <p className="mb-6">If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">14.3 Waiver</h3>
          <p className="mb-6">Our failure to enforce any provision of these Terms does not constitute a waiver of that provision.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">14.4 Assignment</h3>
          <p className="mb-6">You may not assign your rights under these Terms without our written consent. We may assign our rights under these Terms without restriction.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">14.5 Updates to Terms</h3>
          <p className="mb-4">We may update these Terms from time to time. Material changes will be communicated via:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Email notification</li>
            <li>Prominent notice on our Service</li>
            <li>Updated "Last Updated" date</li>
          </ul>
          <p className="mb-6">Continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">15. Contact Information</h2>
          <p className="mb-4">For questions about these Terms of Use, please contact:</p>
          <p className="mb-2"><strong>General Legal Inquiries:</strong> legal@streamnaia.com</p>
          <p className="mb-6"><strong>Copyright/DMCA Claims:</strong> dmca@streamnaia.com</p>
        </section>

        <div className="border-t border-white/20 pt-6 mt-12">
          <p className="text-slate-grey italic text-center">
            By using Naia, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy.
          </p>
        </div>
      </div>
    </LegalLayout>
  );
} 